import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Lock, ArrowUpRight, PartyPopper, Award, Headphones, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api, formatErr, localDay, mediaUrl } from "@/lib/api";
import Celebration from "@/components/Celebration";

const BADGE_CLS = { done: "bg-bt-primary text-bt-bg", current: "border-2 border-bt-primary text-bt-primary" };

function dayState(done, n) {
  if (done.includes(n)) return "done";
  if (n === 1 || done.includes(n - 1)) return "current";
  return "locked";
}

function Day({ d, n, state, onDone, busy }) {
  const [open, setOpen] = useState(state === "current");
  useEffect(() => setOpen(state === "current"), [state]);
  const locked = state === "locked";
  return (
    <li className={`rounded-[28px] border transition-colors ${state === "current" ? "border-bt-primary bg-bt-surface" : "border-bt-border bg-bt-surface"} ${locked ? "opacity-60" : ""}`} data-testid={`challenge-day-${n}`}>
      <button disabled={locked} onClick={() => setOpen(!open)} className="flex w-full items-center gap-4 p-5 text-left sm:p-6" data-testid={`challenge-day-${n}-toggle`}>
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-heading text-lg ${BADGE_CLS[state] || "bg-bt-sage text-bt-sagedeep"}`}>
          {state === "done" && <Check size={20} />}
          {state !== "done" && (locked ? <Lock size={16} /> : n)}
        </span>
        <span>
          <span className="block text-xs font-semibold uppercase tracking-[0.15em] text-bt-sagedeep">Day {n}</span>
          <span className="font-heading text-xl text-bt-heading">{d.title}</span>
        </span>
      </button>
      {open && !locked && (
        <div className="space-y-5 px-5 pb-6 sm:px-6 sm:pl-[88px]">
          {d.audio_url && (
            <div className="flex items-center gap-3 rounded-2xl border border-bt-border p-3" data-testid={`challenge-day-${n}-audio`}>
              <Headphones size={18} className="shrink-0 text-bt-primary" />
              <audio src={mediaUrl(d.audio_url)} controls className="h-10 w-full" />
            </div>
          )}
          {d.intro && <p className="text-lg">{d.intro}</p>}
          <div className="rounded-[22px] bg-bt-sage p-5">
            <p className="eyebrow mb-2">Today's activity</p>
            <p className="text-lg leading-relaxed text-bt-heading">{d.activity}</p>
          </div>
          {d.reflection && <p className="italic"><span className="font-semibold not-italic text-bt-heading">Reflect: </span>{d.reflection}</p>}
          {state === "current" && (
            <button onClick={() => onDone(n)} disabled={busy} className="btn-primary w-full sm:w-auto" data-testid={`challenge-day-${n}-complete`}>
              <Check size={18} /> {busy ? "Saving..." : `Complete day ${n}`}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export default function Challenge() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { settings } = useSite();
  const [c, setC] = useState(null);
  const [done, setDone] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  const load = useCallback(() => {
    api.get(`/challenges/${slug}`).then((r) => setC(r.data)).catch(() => setErr(true));
    if (user) api.get(`/me/challenges/${slug}`).then((r) => setDone(r.data.days_done || []));
  }, [slug, user]);
  useEffect(() => {
    load();
  }, [load]);

  const complete = async (n) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/challenges/${slug}/days/${n}`, { date: localDay() });
      setDone(data.days_done);
      if (n === c.days.length) setCelebrate(true);
      else toast.success(`Day ${n} complete. See you tomorrow.`);
    } catch (e) {
      toast.error(formatErr(e));
    }
    setBusy(false);
  };

  if (err) return <div className="container-bt py-24 text-center"><h1 className="text-4xl">Challenge not found</h1></div>;
  if (!c || user === null) return <div className="min-h-[60vh]" />;
  const total = c.days.length;
  const count = done.length;
  const finished = total > 0 && count >= total;
  const stateOf = (n) => {
    if (user) return dayState(done, n);
    return n === 1 ? "preview" : "locked";
  };

  return (
    <div className="container-bt py-12 sm:py-16" data-testid="challenge-page">
      <div className="mx-auto max-w-3xl">
        {c.image && <img src={mediaUrl(c.image)} alt="" className="mb-10 aspect-[16/8] w-full rounded-[32px] object-cover" />}
        <p className="eyebrow mb-3">Free challenge · {total} days</p>
        <h1 className="text-4xl leading-tight sm:text-5xl" data-testid="challenge-title">{c.title}</h1>
        {c.subtitle && <p className="mt-3 font-heading text-xl italic text-bt-primary">{c.subtitle}</p>}
        <p className="mt-5 text-lg">{c.description}</p>
        <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-bt-sage px-4 py-2 text-sm font-medium text-bt-heading" data-testid="challenge-finished-this-week">
          <Sparkles size={15} className="text-bt-primary" />
          {c.finished_this_week > 0 ? `${c.finished_this_week} ${c.finished_this_week === 1 ? "family" : "families"} finished this week` : "Be one of the first families to finish this week"}
        </p>

        {user ? (
          <div className="mt-8" data-testid="challenge-progress">
            <div className="mb-2 flex justify-between text-sm font-semibold text-bt-heading"><span>Your progress</span><span data-testid="challenge-progress-count">{count} of {total} days</span></div>
            <div className="h-3 overflow-hidden rounded-full bg-bt-sage"><div className="h-full rounded-full bg-bt-primary transition-[width] duration-700" style={{ width: `${total ? (count / total) * 100 : 0}%` }} /></div>
          </div>
        ) : (
          <div className="mt-8 rounded-[28px] bg-bt-sage p-7" data-testid="challenge-signup-prompt">
            <p className="font-heading text-2xl text-bt-heading">Create a free account to start</p>
            <p className="mt-2">We'll save your progress day by day. No payment needed.</p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Link to={`/register?next=/challenge/${slug}`} className="btn-primary" data-testid="challenge-register-cta">Start for free</Link>
              <Link to={`/login?next=/challenge/${slug}`} className="btn-secondary" data-testid="challenge-login-cta">I have an account</Link>
            </div>
          </div>
        )}

        {finished && (
          <div className="mt-8 rounded-[28px] bg-bt-primary p-8 text-bt-bg" data-testid="challenge-complete">
            <PartyPopper className="mb-3" />
            <p className="font-heading text-2xl" style={{ color: "var(--bt-background)" }}>{c.completion_message}</p>
            <Link to={`/challenge/${slug}/certificate`} className="mr-3 mt-5 inline-flex min-h-[52px] items-center gap-2 rounded-full border-2 border-white/40 px-6 font-semibold" data-testid="challenge-certificate-link">
              <Award size={18} /> Get your certificate
            </Link>
            <a href={settings.community.url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex min-h-[52px] items-center gap-2 rounded-full bg-bt-bg px-6 font-semibold text-bt-primary" data-testid="challenge-community-cta">
              {settings.community.cta} <ArrowUpRight size={18} />
            </a>
          </div>
        )}

        {celebrate && <Celebration open={celebrate} onOpenChange={setCelebrate} c={c} />}
        <ol className="mt-10 space-y-3">
          {c.days.map((d, i) => <Day key={`day-${i + 1}`} d={d} n={i + 1} state={stateOf(i + 1)} onDone={complete} busy={busy} />)}
        </ol>
      </div>
    </div>
  );
}
